import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { DividerLine } from "components/icons/DividerLine";
import { InfoFilledSmall } from "components/icons/InfoFilledSmall";
import { SmallInfo } from "components/icons/SmallInfo";
import dayjs from "dayjs";
import { BILLING_CYCLE, BILLING_CYCLE_LABELS } from "dictionaries";
import { createColumnHelper } from "react-table-8.10.7";
import { Cart as CartT } from "store/apis/products";
import {
  FOREIGN_QUALIFICATION,
  FORM_5471_FOREIGN_SUBSIDIARY_REPORTING,
  FORM_5472_REPORTABLE_TRANSACTIONS,
  STATE_ANNUAL_REPORTS,
  US_BOOKKEEPING_ONE_TIME,
} from "types/Models/services";
import { ArrayElement } from "types/utils/ArrayElement";
import { formatDate } from "utils/formatDate";
import { PERIOD_MAP } from "./CartModal";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { CartItemQuantityUpdate } from "./CartItemQuantityUpdate";
import { CartItemAmountUpdate } from "./CartItemAmountUpdate";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { DarkFilledInfo } from "components/icons/DarkFilledInfo";
import { ACCOUNTING_METHODS } from "constants/bookkeeping";
import { ReactNode } from "react";

dayjs.extend(isSameOrBefore);

const NO_EXTRA_METADATA: ReactNode[] = [];

export const createCartColumn =
  createColumnHelper<ArrayElement<CartT["cart_items"]>>();

export const PAYMENT_ACCOUNTING_METHODS_MAP = {
  CASH: (
    <div className="t-flex t-items-center t-gap-1">
      Cash method
      <ToolTip
        text={
          <div className="t-p-3 t-text-left t-w-80">
            <div className="t-text-subtext">Cash</div>
            <div className="t-text-body-sm t-mt-1">
              The cash method provides immediate recognition of revenue and
              expenses when money is received or spent.
            </div>
            <div className="t-mt-4 t-text-body-sm">
              Example: If you complete a service worth $100 on March 15 and the
              customer pays you on April 10, you record the $100 as income on
              April 10 when the cash is received.
            </div>
          </div>
        }
      >
        <img src={FilingInfoIcon} alt="info" />
      </ToolTip>
    </div>
  ),
  ACCRUAL: (
    <div className="t-flex t-items-center t-gap-1">
      Accrual method
      <ToolTip
        text={
          <div className="t-p-3 t-text-left t-w-80">
            <div className="t-text-subtext">Accrual</div>
            <div className="t-text-body-sm t-mt-1">
              The accrual method focuses on anticipated revenue and expenses,
              recording them when they are earned or incurred, regardless of
              cash movement.
            </div>
            <div className="t-mt-4 t-text-body-sm">
              Example: If you complete a service worth $100 on March 15 but get
              paid on April 10, you still record $100 as income on March 15.
            </div>
          </div>
        }
      >
        <img src={FilingInfoIcon} alt="info" />
      </ToolTip>
    </div>
  ),
};

export const ItemColumn = ({
  cartItem,
  extraMetadata = NO_EXTRA_METADATA,
}: {
  cartItem: ArrayElement<CartT["cart_items"]>;
  extraMetadata?: ReactNode[];
}) => {
  const { isCustomer } = useRoleBasedView();
  const {
    ra_state_name,
    sales_tax_state_name,
    tier_information,
    accounting_method,
    product_details: {
      season,
      has_balancing_payment,
      state,
      product_name,
      slabs,
      quarter,
      product_info,
      base_task_key,
    },
    parent_template,
    subscription,
  } = cartItem;

  const billingLabel = subscription?.billing_cycle
    ? BILLING_CYCLE_LABELS[subscription.billing_cycle]
    : "";
  const recurringType = subscription
    ? `Recurring${billingLabel ? ` (${billingLabel})` : ""}`
    : "";

  const metadataItems = [
    accounting_method && PAYMENT_ACCOUNTING_METHODS_MAP[accounting_method],
    quarter,
    ra_state_name,
    sales_tax_state_name,
    tier_information?.range,
    recurringType,
    state?.name,
    ...extraMetadata,
  ].filter(Boolean);

  return (
    <div className="t-flex t-flex-col t-gap-1.5">
      <div className="t-flex t-items-center t-gap-1">
        <p className="t-m-0">{product_name}</p>
        {(base_task_key === STATE_ANNUAL_REPORTS ||
          base_task_key === FOREIGN_QUALIFICATION) && (
          <ToolTip text="Government fees included in this amount">
            <span className="t-text-text-30 t-flex">
              <SmallInfo />
            </span>
          </ToolTip>
        )}
      </div>
      {metadataItems.length > 0 && (
        <p className="t-m-0 t-flex sm:t-flex-wrap t-gap-1.5 t-items-center t-text-body-sm t-text-text-30">
          {metadataItems.map((item, index) => (
            <span key={index} className="t-flex t-gap-1.5 t-items-center">
              {index > 0 && (
                <span className="t-text-neutral-20">
                  <DividerLine />
                </span>
              )}
              <span>{item}</span>
            </span>
          ))}
        </p>
      )}
      {(has_balancing_payment || product_info) && (
        <p className="t-m-0 t-flex t-gap-1 t-items-center t-text-body-sm">
          <span className="t-text-orange t-flex">
            <InfoFilledSmall color="currentColor" />
          </span>
          <span className="t-italic t-font-light t-text-text-30">
            May have extra charges.
          </span>

          {product_info && (
            <ToolTip text={product_info} propClass="t-w-72 !t-text-left">
              <img src={FilingInfoIcon} alt="info" />
            </ToolTip>
          )}

          <Modal.Root>
            <Modal.Trigger asChild>
              {slabs && (
                <button className="all:unset t-text-purple t-italic t-font-light hover:t-underline">
                  Learn more
                </button>
              )}
            </Modal.Trigger>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>{product_name}</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body>
                {slabs && (
                  <div
                    className="t-text-body-sm"
                    dangerouslySetInnerHTML={{
                      __html: slabs,
                    }}
                  />
                )}
              </Modal.Body>

              <Modal.Footer>
                <Modal.RawClose asChild>
                  <Button block>Go back to cart</Button>
                </Modal.RawClose>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Root>
        </p>
      )}
      {!isCustomer && parent_template?.parent_template_label && (
        <div className="t-flex t-items-center t-gap-1">
          <DarkFilledInfo className="t-text-blue" />
          <div className="t-text-body-sm t-text-text-30">
            {parent_template.parent_template_label}
          </div>
        </div>
      )}
    </div>
  );
};

export const QuantityColumn = () => {
  return createCartColumn.accessor("quantity", {
    header: "QTY",
    size: 3,
    cell: (info) => {
      const rowData = info.row.original;
      const { quantity, product_details, uuid } = rowData;
      const shouldShowQuantityUpdator =
        product_details.base_task_key ===
          FORM_5471_FOREIGN_SUBSIDIARY_REPORTING ||
        product_details.base_task_key === FORM_5472_REPORTABLE_TRANSACTIONS;

      return (
        <div className="t-text-text-30 t-text-body-sm">
          {shouldShowQuantityUpdator ? (
            <CartItemQuantityUpdate
              quantity={quantity}
              cartItemId={uuid}
              // @ts-ignore
              entityId={info.table.options.meta?.entityId}
            />
          ) : (
            quantity || ""
          )}
        </div>
      );
    },
  });
};

export const cartColumn = {
  name: () =>
    createCartColumn.accessor("product_details.product_name", {
      header: "Item",
      cell: (info) => {
        return <ItemColumn cartItem={info.row.original} />;
      },
      size: 50,
    }),
  season: () =>
    createCartColumn.accessor("product_details.season", {
      header: "Period",
      cell: (info) => {
        const season = info.getValue();
        const billingCycle = info.row.original.subscription?.billing_cycle;

        if (info.row.original.product_details.from_date) {
          return (
            <ConditionalToolTip
              condition={
                info.row.original.subscription &&
                `The first deduction of this payment will happen on ${formatDate(
                  dayjs(info.row.original.product_details.from_date).add(
                    1,
                    "month"
                  )
                )}`
              }
              align="start"
            >
              <div className="t-text-text-30 t-text-body-sm t-flex t-gap-1 t-items-center">
                <span>
                  Starts{" "}
                  {dayjs(info.row.original.product_details.from_date).format(
                    "MMM YYYY"
                  )}{" "}
                  {info.row.original.product_details.to_date && (
                    <>
                      -{" "}
                      {dayjs(info.row.original.product_details.to_date).format(
                        "MMM YYYY"
                      )}
                    </>
                  )}
                </span>
                {info.row.original.subscription && (
                  <img src={FilingInfoIcon} alt="info" />
                )}
              </div>
            </ConditionalToolTip>
          );
        }

        if (!info.row.original.subscription) {
          return (
            <div className="t-text-text-30 t-text-body-sm">
              {season ? `Season ${season}` : "-"}
            </div>
          );
        }

        if (!billingCycle) {
          return "-";
        }

        const validTill = formatDate(
          dayjs().add(1, PERIOD_MAP[billingCycle] as dayjs.ManipulateType)
        );

        return (
          <div className="t-text-text-30 t-text-body-sm">
            {formatDate(dayjs())} - <br />
            {validTill}
          </div>
        );
      },
      size: 24,
    }),

  rate: () =>
    createCartColumn.accessor("unit_price", {
      header: "Rate",
      cell: (info) => (
        <PriceCell
          cartItem={info.row.original}
          total={info.getValue()}
          // @ts-ignore
          entityId={info.table.options.meta?.entityId as string}
        />
      ),
      size: 20,
    }),

  total: () =>
    createCartColumn.accessor("total", {
      header: "Total",
      cell: (info) => <AmountSuperScript amount={info.getValue()} />,
      size: 20,
    }),
};

const CartAmount = ({
  cartItemId,
  unitPrice,
  entityId,
  subtotal,
}: {
  cartItemId: string;
  unitPrice: number;
  entityId: string;
  subtotal: number;
}) => {
  const { isAdmin } = useRoleBasedView();

  if (isAdmin) {
    return (
      <CartItemAmountUpdate
        cartItemId={cartItemId}
        unitPrice={unitPrice}
        entityId={entityId}
      />
    );
  }

  return <AmountSuperScript amount={unitPrice} />;
};

const PriceCell = ({
  cartItem,
  total,
  entityId,
}: {
  cartItem: ArrayElement<CartT["cart_items"]>;
  total: number;
  entityId: string;
}) => {
  const {
    discount,
    subtotal,
    accounting_method,
    unit_price,
    uuid,
    after_discount_unit_price,
  } = cartItem;
  const isDiscounted = discount && discount.discount_value > 0;

  if (cartItem.product_details.base_task_key === US_BOOKKEEPING_ONE_TIME) {
    const startPricing =
      accounting_method === ACCOUNTING_METHODS.CASH ? 199 : 399;

    if (!isNaN(Number(subtotal)) && Number(subtotal) !== 0) {
      return (
        <CartAmount
          cartItemId={uuid}
          unitPrice={unit_price}
          entityId={entityId}
          subtotal={subtotal}
        />
      );
    }

    return (
      <div>
        <div className="t-text-subtext t-text-text-60">Post paid</div>
        <div className="t-text-text-30 t-text-body-sm">
          Starts at ${startPricing}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <p
        className={classNames("t-m-0 t-flex t-items-center", {
          "t-line-through t-text-body-sm t-text-text-30": isDiscounted,
        })}
      >
        <CartAmount
          cartItemId={uuid}
          unitPrice={unit_price}
          entityId={entityId}
          subtotal={subtotal}
        />
        {cartItem.subscription && (
          <span className="t-text-text-30 t-flex t-gap-1 t-text-nowrap t-items-center">
            /{BILLING_CYCLE[cartItem.subscription?.billing_cycle]}
            {!isDiscounted && !cartItem.product_details.from_date && (
              <ToolTip
                text={`Renews on ${formatDate(
                  dayjs().add(
                    1,
                    PERIOD_MAP[
                      cartItem.subscription?.billing_cycle
                    ] as dayjs.ManipulateType
                  )
                )}`}
                side="bottom"
              >
                <span className="t-text-text-30">
                  <SmallInfo />
                </span>
              </ToolTip>
            )}
          </span>
        )}
      </p>
      {Boolean(discount) && discount && discount.discount_value > 0 && (
        <p className="t-m-0 t-flex t-flex-nowrap t-items-center">
          <AmountSuperScript amount={after_discount_unit_price ?? total} />
          {cartItem.subscription && (
            <span className="t-text-text-30 t-flex t-gap-1 t-flex-nowrap t-items-center t-text-nowrap">
              /{BILLING_CYCLE[cartItem.subscription?.billing_cycle]}
              {isDiscounted && !cartItem.from_date && (
                <ToolTip
                  text={`Renews on ${formatDate(
                    dayjs().add(
                      1,
                      PERIOD_MAP[
                        cartItem.subscription?.billing_cycle
                      ] as dayjs.ManipulateType
                    )
                  )}`}
                  side="bottom"
                >
                  <span className="t-text-text-30">
                    <SmallInfo />
                  </span>
                </ToolTip>
              )}
            </span>
          )}
        </p>
      )}
    </div>
  );
};
